import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon,
  Add as AddIcon,
  Category as CategoryIcon,
  Circle as CircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  HeadsetMic as HeadsetMicIcon,
  MoreVert as MoreVertIcon,
  People as PeopleIcon,
  SubdirectoryArrowRight as SubIcon,
} from '@mui/icons-material';
import type { AtendimentoSetor, AtendimentoSubsetor } from '../../types';
import atendimentoSetorService from '../../services/atendimentoSetorService';
import userService from '../../services/userService';
import type { User } from '../../types';

type DialogTipo = 'setor' | 'subsetor';
type Setor = AtendimentoSetor;
type Subsetor = AtendimentoSubsetor;

interface FormState {
  nome: string;
  descricao: string;
  cor: string;
  usuarios: number[];
  ativo: boolean;
}

const CORES_PRESET = [
  '#1565c0', '#2e7d32', '#c62828', '#e65100',
  '#6a1b9a', '#0277bd', '#f9a825', '#4e342e',
  '#00695c', '#283593',
];

const formVazio: FormState = {
  nome: '',
  descricao: '',
  cor: CORES_PRESET[0],
  usuarios: [],
  ativo: true,
};

const AtendimentoSetoresTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [setores, setSetores] = useState<Setor[]>([]);
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [sucessoMsg, setSucessoMsg] = useState('');
  const [erroMsg, setErroMsg] = useState('');

  const [dialogAberto, setDialogAberto] = useState(false);
  const [dialogTipo, setDialogTipo] = useState<DialogTipo>('setor');
  const [setorPai, setSetorPai] = useState<Setor | null>(null);
  const [editandoSetor, setEditandoSetor] = useState<Setor | null>(null);
  const [editandoSubsetor, setEditandoSubsetor] = useState<Subsetor | null>(null);
  const [form, setForm] = useState<FormState>(formVazio);

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteAlvo, setDeleteAlvo] = useState<{ tipo: DialogTipo; id: number; nome: string; setorId?: number } | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAlvo, setMenuAlvo] = useState<{ tipo: DialogTipo; setor?: Setor; subsetor?: Subsetor; setorId?: number } | null>(null);

  const carregarSetores = useCallback(async () => {
    setLoading(true);
    try {
      const response = await atendimentoSetorService.getAll();
      if (response.success && response.data) {
        const dados = response.data;
        setSetores(dados);
        setExpandidos((prev) => {
          if (prev.size > 0) return prev;
          return new Set(dados.filter((setor) => setor.subsetores.length > 0).map((setor) => setor.id));
        });
      }
    } catch {
      setErroMsg('Erro ao carregar setores de atendimento.');
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarUsuarios = useCallback(async () => {
    setLoadingUsuarios(true);
    try {
      const response = await userService.getAllUsers({ limit: 1000, sortBy: 'nome', sortOrder: 'ASC' });
      setUsuarios(response.users || []);
    } catch {
      setUsuarios([]);
    } finally {
      setLoadingUsuarios(false);
    }
  }, []);

  useEffect(() => {
    carregarSetores();
    carregarUsuarios();
  }, [carregarSetores, carregarUsuarios]);

  const kpis = useMemo(() => {
    const totalSetores = setores.length;
    const totalAtivos = setores.filter((setor) => setor.ativo).length;
    const totalSubsetores = setores.reduce((acc, setor) => acc + setor.subsetores.length, 0);
    const totalAtendentes = setores.reduce(
      (acc, setor) => acc + (setor.usuarios?.length || 0) + setor.subsetores.reduce((subAcc, sub) => subAcc + (sub.usuarios?.length || 0), 0),
      0
    );

    return [
      { label: 'Total de Setores', valor: totalSetores, cor: 'primary.main', sub: 'cadastrados' },
      { label: 'Setores Ativos', valor: totalAtivos, cor: '#2e7d32', sub: 'em operacao' },
      { label: 'Subsetores', valor: totalSubsetores, cor: '#e65100', sub: 'no total' },
      { label: 'Atendentes', valor: totalAtendentes, cor: '#6a1b9a', sub: 'distribuidos' },
    ];
  }, [setores]);

  const toggleExpand = (id: number) => {
    setExpandidos((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const abrirNovoSetor = () => {
    setDialogTipo('setor');
    setEditandoSetor(null);
    setEditandoSubsetor(null);
    setSetorPai(null);
    setForm(formVazio);
    setErroMsg('');
    setDialogAberto(true);
    setMenuAnchor(null);
  };

  const abrirNovoSubsetor = (setor: Setor) => {
    setDialogTipo('subsetor');
    setEditandoSetor(null);
    setEditandoSubsetor(null);
    setSetorPai(setor);
    setForm({ ...formVazio, cor: setor.cor });
    setErroMsg('');
    setDialogAberto(true);
    setMenuAnchor(null);
  };

  const abrirEditarSetor = (setor: Setor) => {
    setDialogTipo('setor');
    setEditandoSetor(setor);
    setEditandoSubsetor(null);
    setSetorPai(null);
    setForm({
      nome: setor.nome,
      descricao: setor.descricao || '',
      cor: setor.cor,
      usuarios: setor.usuarios || [],
      ativo: setor.ativo,
    });
    setErroMsg('');
    setDialogAberto(true);
    setMenuAnchor(null);
  };

  const abrirEditarSubsetor = (subsetor: Subsetor, setorId: number) => {
    setDialogTipo('subsetor');
    setEditandoSetor(null);
    setEditandoSubsetor(subsetor);
    setSetorPai(setores.find((setor) => setor.id === setorId) || null);
    setForm({
      nome: subsetor.nome,
      descricao: subsetor.descricao || '',
      cor: subsetor.cor,
      usuarios: subsetor.usuarios || [],
      ativo: subsetor.ativo,
    });
    setErroMsg('');
    setDialogAberto(true);
    setMenuAnchor(null);
  };

  const fecharDialog = () => {
    setDialogAberto(false);
    setErroMsg('');
  };

  const handleSalvar = async () => {
    if (!form.nome.trim()) {
      setErroMsg('Nome e obrigatorio.');
      return;
    }

    setSalvando(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || null,
        cor: form.cor,
        usuarios: form.usuarios,
        ativo: form.ativo,
        parent_id: dialogTipo === 'subsetor' ? setorPai?.id || null : null,
        icone: dialogTipo === 'setor' ? 'folder' : null,
      };

      if (dialogTipo === 'setor' && editandoSetor) {
        await atendimentoSetorService.update(editandoSetor.id, payload);
        setSucessoMsg(`Setor "${form.nome}" atualizado com sucesso!`);
      } else if (dialogTipo === 'setor') {
        await atendimentoSetorService.create(payload);
        setSucessoMsg(`Setor "${form.nome}" criado com sucesso!`);
      } else if (editandoSubsetor) {
        await atendimentoSetorService.update(editandoSubsetor.id, payload);
        setSucessoMsg(`Subsetor "${form.nome}" atualizado com sucesso!`);
      } else if (setorPai) {
        await atendimentoSetorService.create(payload);
        setExpandidos((prev) => new Set([...prev, setorPai.id]));
        setSucessoMsg(`Subsetor "${form.nome}" criado em "${setorPai.nome}"!`);
      }

      fecharDialog();
      await carregarSetores();
      setTimeout(() => setSucessoMsg(''), 3500);
    } catch {
      setErroMsg('Erro ao salvar setor de atendimento.');
    } finally {
      setSalvando(false);
    }
  };

  const confirmarExclusao = async () => {
    if (!deleteAlvo) return;

    setSalvando(true);
    try {
      await atendimentoSetorService.delete(deleteAlvo.id);
      setSucessoMsg(`"${deleteAlvo.nome}" excluido com sucesso.`);
      setDeleteDialog(false);
      setDeleteAlvo(null);
      await carregarSetores();
      setTimeout(() => setSucessoMsg(''), 3000);
    } catch {
      setErroMsg('Erro ao excluir setor de atendimento.');
    } finally {
      setSalvando(false);
    }
  };

  const abrirConfirmacaoExclusao = () => {
    if (!menuAlvo) return;
    const alvo = menuAlvo.tipo === 'setor' ? menuAlvo.setor : menuAlvo.subsetor;
    if (!alvo) return;

    setDeleteAlvo({
      tipo: menuAlvo.tipo,
      id: alvo.id,
      nome: alvo.nome,
      setorId: menuAlvo.setorId,
    });
    setDeleteDialog(true);
    setMenuAnchor(null);
  };

  const getUsuariosDoItem = (usuarioIds?: number[] | null) => (
    usuarios.filter((usuario) => (usuarioIds || []).includes(usuario.id))
  );

  const renderUsuarios = (usuarioIds?: number[] | null, compact = false) => {
    const usuariosDoItem = getUsuariosDoItem(usuarioIds);
    if (usuariosDoItem.length === 0) return null;

    const limite = compact ? 2 : 3;
    const visiveis = usuariosDoItem.slice(0, limite);
    const restantes = usuariosDoItem.length - visiveis.length;

    return (
      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.6}>
        {visiveis.map((usuario) => (
          <Chip
            key={usuario.id}
            icon={<PeopleIcon sx={{ fontSize: compact ? '11px !important' : '12px !important' }} />}
            label={usuario.nome}
            size="small"
            variant="outlined"
            sx={{ height: compact ? 18 : 20, fontSize: compact ? '0.58rem' : '0.62rem', maxWidth: compact ? 120 : 150 }}
          />
        ))}
        {restantes > 0 && (
          <Chip label={`+${restantes}`} size="small" variant="outlined" sx={{ height: compact ? 18 : 20, fontSize: compact ? '0.58rem' : '0.62rem' }} />
        )}
      </Box>
    );
  };

  const renderMetricas = (item: Pick<Setor, 'cor' | 'usuarios' | 'atendimentos_abertos'>, compact = false) => (
    <Box display="flex" gap={2.5} mr={1}>
      <Box textAlign="center">
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: compact ? '0.6rem' : '0.62rem' }}>
          Atendentes
        </Typography>
        <Box display="flex" alignItems="center" gap={0.3}>
          <PeopleIcon sx={{ fontSize: compact ? 12 : 13, color: item.cor }} />
          <Typography variant={compact ? 'caption' : 'body2'} fontWeight={700} color={item.cor}>
            {item.usuarios?.length || 0}
          </Typography>
        </Box>
      </Box>
      <Box textAlign="center">
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: compact ? '0.6rem' : '0.62rem' }}>
          Em aberto
        </Typography>
        <Box display="flex" alignItems="center" gap={0.3}>
          <HeadsetMicIcon sx={{ fontSize: compact ? 12 : 13, color: item.atendimentos_abertos > 0 ? '#e65100' : 'text.disabled' }} />
          <Typography variant={compact ? 'caption' : 'body2'} fontWeight={700} color={item.atendimentos_abertos > 0 ? '#e65100' : 'text.disabled'}>
            {item.atendimentos_abertos || 0}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderSetor = (setor: Setor) => {
    const aberto = expandidos.has(setor.id);

    return (
      <Paper
        key={setor.id}
        sx={{
          mb: 1.5,
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          opacity: setor.ativo ? 1 : 0.65,
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.5,
            py: 0.8,
            borderLeft: `5px solid ${setor.cor}`,
            bgcolor: aberto ? `${setor.cor}08` : 'background.paper',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => toggleExpand(setor.id)}
        >
          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: `${setor.cor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AccountTreeIcon sx={{ color: setor.cor, fontSize: 18 }} />
          </Box>

          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="subtitle1" fontWeight={700} noWrap>{setor.nome}</Typography>
              <Chip label={setor.ativo ? 'Ativo' : 'Inativo'} size="small" color={setor.ativo ? 'success' : 'default'} sx={{ height: 18, fontSize: '0.62rem' }} />
              {setor.subsetores.length > 0 && (
                <Chip label={`${setor.subsetores.length} subsetor${setor.subsetores.length > 1 ? 'es' : ''}`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
              )}
            </Box>
            {setor.descricao && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 420 }}>
                {setor.descricao}
              </Typography>
            )}
            {renderUsuarios(setor.usuarios)}
          </Box>

          {!isMobile && renderMetricas(setor)}

          <Box display="flex" alignItems="center" gap={0.3} onClick={(event) => event.stopPropagation()}>
            <Tooltip title="Adicionar subsetor">
              <IconButton size="small" color="primary" onClick={() => abrirNovoSubsetor(setor)}>
                <SubIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={(event) => { setMenuAnchor(event.currentTarget); setMenuAlvo({ tipo: 'setor', setor }); }}>
              <MoreVertIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>

          {aberto ? <ExpandLessIcon sx={{ color: 'text.secondary', fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
        </Box>

        <Collapse in={aberto}>
          {setor.subsetores.length === 0 ? (
            <Box sx={{ px: 4, py: 2, bgcolor: 'action.hover', borderTop: `1px dashed ${theme.palette.divider}` }} display="flex" alignItems="center" gap={1}>
              <SubIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                Nenhum subsetor cadastrado.
              </Typography>
            </Box>
          ) : (
            setor.subsetores.map((sub, idx) => (
              <Box key={sub.id}>
                {idx > 0 && <Divider sx={{ ml: 6 }} />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 0.5,
                    pl: 4.5,
                    bgcolor: 'background.paper',
                    borderLeft: `5px solid ${sub.cor}`,
                    opacity: sub.ativo ? 1 : 0.55,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <SubIcon sx={{ color: 'text.disabled', fontSize: 16, flexShrink: 0 }} />
                  <CircleIcon sx={{ fontSize: 10, color: sub.cor, flexShrink: 0 }} />

                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={600} noWrap>{sub.nome}</Typography>
                      <Chip label={sub.ativo ? 'Ativo' : 'Inativo'} size="small" color={sub.ativo ? 'success' : 'default'} sx={{ height: 16, fontSize: '0.58rem' }} />
                    </Box>
                    {sub.descricao && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 350, fontSize: '0.7rem' }}>
                        {sub.descricao}
                      </Typography>
                    )}
                    {renderUsuarios(sub.usuarios, true)}
                  </Box>

                  {!isMobile && renderMetricas(sub, true)}

                  <IconButton size="small" onClick={(event) => { setMenuAnchor(event.currentTarget); setMenuAlvo({ tipo: 'subsetor', subsetor: sub, setorId: setor.id }); }}>
                    <MoreVertIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Box>
            ))
          )}

          <Box
            sx={{
              px: 4.5,
              py: 0.5,
              borderTop: `1px dashed ${theme.palette.divider}`,
              bgcolor: `${setor.cor}04`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { bgcolor: `${setor.cor}10` },
            }}
            onClick={() => abrirNovoSubsetor(setor)}
          >
            <AddIcon sx={{ fontSize: 14, color: setor.cor }} />
            <Typography variant="caption" color={setor.cor} fontWeight={600} sx={{ fontSize: '0.72rem' }}>
              Adicionar subsetor
            </Typography>
          </Box>
        </Collapse>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Setores de Atendimento</Typography>
            <Typography variant="body2" color="text.secondary">Organize as equipes em setores e subsetores</Typography>
          </Box>
        </Box>
        {!isMobile && (
          <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={abrirNovoSetor}>
            Novo Setor
          </Button>
        )}
      </Box>

      {sucessoMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucessoMsg('')}>{sucessoMsg}</Alert>}
      {erroMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroMsg('')}>{erroMsg}</Alert>}

      <Grid container spacing={1.5} mb={1.5}>
        {kpis.map((kpi) => (
          <Grid item xs={6} sm={3} key={kpi.label}>
            <Card>
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6" fontWeight={700} color={kpi.cor}>{kpi.valor}</Typography>
                <Typography variant="caption" color="text.secondary">{kpi.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" mt={2}>Carregando setores...</Typography>
        </Paper>
      ) : setores.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <CategoryIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>Nenhum setor cadastrado</Typography>
          <Typography variant="body2" color="text.disabled" mb={2}>Crie o primeiro setor para organizar os atendimentos</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovoSetor}>Criar Setor</Button>
        </Paper>
      ) : (
        setores.map((setor) => renderSetor(setor))
      )}

      {isMobile && (
        <Box sx={{ position: 'fixed', bottom: 86, right: 16 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={abrirNovoSetor} sx={{ borderRadius: 3, boxShadow: 4 }}>
            Novo Setor
          </Button>
        </Box>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{ sx: { borderRadius: 2, minWidth: 170 } }}>
        <MenuItem
          onClick={() => {
            if (menuAlvo?.tipo === 'setor' && menuAlvo.setor) abrirEditarSetor(menuAlvo.setor);
            if (menuAlvo?.tipo === 'subsetor' && menuAlvo.subsetor) abrirEditarSubsetor(menuAlvo.subsetor, menuAlvo.setorId!);
          }}
          sx={{ fontSize: '0.8rem' }}
        >
          <EditIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} /> Editar
        </MenuItem>
        {menuAlvo?.tipo === 'setor' && (
          <MenuItem onClick={() => abrirNovoSubsetor(menuAlvo.setor!)} sx={{ fontSize: '0.8rem' }}>
            <SubIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} /> Adicionar subsetor
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={abrirConfirmacaoExclusao} sx={{ fontSize: '0.8rem', color: 'error.main' }}>
          <DeleteIcon sx={{ fontSize: 16, mr: 1 }} /> Excluir
        </MenuItem>
      </Menu>

      <Dialog open={dialogAberto} onClose={fecharDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            {dialogTipo === 'setor' ? <AccountTreeIcon color="primary" /> : <SubIcon color="primary" />}
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {editandoSetor || editandoSubsetor ? 'Editar' : 'Novo'} {dialogTipo === 'setor' ? 'Setor' : 'Subsetor'}
              </Typography>
              {dialogTipo === 'subsetor' && setorPai && (
                <Typography variant="caption" color="text.secondary">em: {setorPai.nome}</Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {erroMsg && <Alert severity="error" onClose={() => setErroMsg('')}>{erroMsg}</Alert>}

          <TextField
            label="Nome *"
            size="small"
            fullWidth
            value={form.nome}
            onChange={(event) => setForm({ ...form, nome: event.target.value })}
            placeholder={dialogTipo === 'setor' ? 'Ex: Suporte Tecnico' : 'Ex: Fibra Optica'}
            autoFocus
          />

          <TextField
            label="Descricao"
            size="small"
            fullWidth
            multiline
            rows={2}
            value={form.descricao}
            onChange={(event) => setForm({ ...form, descricao: event.target.value })}
            placeholder="Descreva brevemente a funcao deste setor..."
          />

          <Autocomplete
            multiple
            size="small"
            options={usuarios}
            loading={loadingUsuarios}
            value={usuarios.filter((usuario) => form.usuarios.includes(usuario.id))}
            getOptionLabel={(option) => `${option.nome} (${option.usuario})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, selecionados) => setForm({ ...form, usuarios: selecionados.map((usuario) => usuario.id) })}
            renderTags={(selected, getTagProps) => selected.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.nome}
                size="small"
              />
            ))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuarios"
                placeholder="Selecione os usuarios"
                helperText="Usuarios pertencentes a este setor"
              />
            )}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.8}>Cor de identificacao</Typography>
            <Box display="flex" gap={0.8} flexWrap="wrap" alignItems="center">
              {CORES_PRESET.map((cor) => (
                <Box
                  key={cor}
                  onClick={() => setForm({ ...form, cor })}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: cor,
                    cursor: 'pointer',
                    border: form.cor === cor ? `3px solid ${theme.palette.text.primary}` : '3px solid transparent',
                    '&:hover': { transform: 'scale(1.12)' },
                  }}
                />
              ))}
              <Box display="flex" alignItems="center" gap={0.8} ml={1}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: form.cor }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{form.cor}</Typography>
              </Box>
            </Box>
          </Box>

          <FormControlLabel
            control={<Switch checked={form.ativo} onChange={(event) => setForm({ ...form, ativo: event.target.checked })} color="success" size="small" />}
            label={<Typography variant="body2">{form.ativo ? 'Ativo - aparece na fila de atendimento' : 'Inativo - oculto na fila'}</Typography>}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={fecharDialog} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvar} disabled={!form.nome.trim() || salvando}>
            {salvando ? 'Salvando...' : editandoSetor || editandoSubsetor ? 'Salvar alteracoes' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar exclusao</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Tem certeza que deseja excluir <strong>"{deleteAlvo?.nome}"</strong>?
            {deleteAlvo?.tipo === 'setor' && (
              <Typography variant="caption" color="error" display="block" mt={1}>
                Todos os subsetores vinculados tambem serao excluidos.
              </Typography>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setDeleteDialog(false)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={confirmarExclusao} disabled={salvando}>
            {salvando ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AtendimentoSetoresTab;
